/**
 * Rückmeldungen: Fehler, Wünsche und Fragen aus dem Einsatz.
 *
 * Rückmeldung aus dem ersten Test bei Wania: "Ticketsystem einbauen". Bisher
 * kamen Rückmeldungen als Liste im Gespräch — ohne Screenshot, ohne Projekt,
 * und ohne dass später nachzusehen war, was daraus geworden ist.
 *
 * Mit dem Ticket geht mit, wo es entstanden ist: Projekt, Version, Gerät,
 * Fenstergröße. Ein Fehler, den man nicht nachstellen kann, bleibt liegen.
 *
 * Tickets laufen nicht durch den Abgleich. Wer meldet, braucht Verbindung —
 * das hält nichts auf, die Arbeit am Dach geht ohne Ticket weiter.
 */

import { useCallback, useEffect, useState } from 'react';

import { rufeServer } from '../daten/abgleich.ts';

type Art = 'fehler' | 'wunsch' | 'frage';
type Status = 'offen' | 'in_arbeit' | 'erledigt';
type Prioritaet = 'niedrig' | 'normal' | 'hoch';

interface Ticket {
  id: string;
  nummer: number;
  titel: string;
  beschreibung: string | null;
  art: Art;
  status: Status;
  prioritaet: Prioritaet;
  loesung: string | null;
  projekt_id: string | null;
  projekt_name: string | null;
  kontext: Record<string, unknown> | null;
  erstellt_von_name: string | null;
  angelegt_am: string;
  erledigt_am: string | null;
  anhaenge: Array<{ id: string; dateiname: string | null; mime: string }>;
}

interface Verlaufseintrag {
  benutzer_name: string | null;
  aktion: string;
  details: string | null;
  zeit: string;
}

const ARTNAMEN: Record<Art, string> = { fehler: 'Fehler', wunsch: 'Wunsch', frage: 'Frage' };
const STATUSNAMEN: Record<Status, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  erledigt: 'Erledigt',
};
const PRIORITAETSNAMEN: Record<Prioritaet, string> = {
  niedrig: 'Niedrig',
  normal: 'Normal',
  hoch: 'Hoch',
};

const datum = (wert: string): string =>
  new Date(wert).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' });

async function fehlerAus(antwort: Response): Promise<string> {
  const daten = (await antwort.json().catch(() => ({}))) as { fehler?: string };
  return daten.fehler ?? `Der Server antwortet mit ${antwort.status}.`;
}

/** Wie viele Tickets offen sind — für die Zahl am Knopf. */
export function useOffeneTickets(online: boolean): number {
  const [anzahl, setAnzahl] = useState(0);
  useEffect(() => {
    if (!online) return;
    let aktiv = true;
    const hole = (): void => {
      rufeServer('/api/tickets?status=offen')
        .then((a) => (a.ok ? a.json() : null))
        .then((d: { offen?: number } | null) => {
          if (aktiv && d?.offen !== undefined) setAnzahl(d.offen);
        })
        .catch(() => undefined);
    };
    hole();
    const takt = window.setInterval(hole, 120_000);
    window.addEventListener('pv:tickets-geaendert', hole);
    return () => {
      aktiv = false;
      window.clearInterval(takt);
      window.removeEventListener('pv:tickets-geaendert', hole);
    };
  }, [online]);
  return anzahl;
}

const meldeAenderung = (): void => {
  window.dispatchEvent(new CustomEvent('pv:tickets-geaendert'));
};

export function Tickets({
  online,
  projekt,
  version,
  istVerwalter,
  onSchliessen,
}: {
  online: boolean;
  /** Das offene Projekt — das Ticket hängt dann daran. */
  projekt: { id: string; name: string } | null;
  version: string;
  istVerwalter: boolean;
  onSchliessen: () => void;
}): React.JSX.Element {
  const [ansicht, setAnsicht] = useState<'offen' | 'erledigt' | 'alle'>('offen');
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [offenesTicket, setOffenesTicket] = useState<string | null>(null);

  const lade = useCallback(async () => {
    if (!online) return;
    try {
      const antwort = await rufeServer('/api/tickets');
      if (!antwort.ok) throw new Error(await fehlerAus(antwort));
      const daten = (await antwort.json()) as { tickets: Ticket[] };
      setTickets(daten.tickets);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [online]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const sichtbar = (tickets ?? []).filter((t) =>
    ansicht === 'alle' ? true : ansicht === 'erledigt' ? t.status === 'erledigt' : t.status !== 'erledigt',
  );

  return (
    <div className="druckrahmen" onClick={onSchliessen}>
      <div
        className="druckblatt"
        style={{ maxWidth: '52rem' }}
        onClick={(ereignis) => ereignis.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <p className="marke">Rückmeldungen</p>
            <h1>Fehler, Wünsche, Fragen</h1>
          </div>
          <button onClick={onSchliessen}>Schließen</button>
        </div>

        {!online && (
          <div className="hinweis hinweis--warnung">
            Rückmeldungen brauchen Verbindung. Die Arbeit am Projekt geht ohne sie weiter — bitte
            später melden.
          </div>
        )}
        {fehler && <div className="hinweis hinweis--fehler">{fehler}</div>}

        {online && !neu && !offenesTicket && (
          <button className="wichtig" onClick={() => setNeu(true)}>
            Neue Rückmeldung
          </button>
        )}

        {neu && (
          <NeuesTicket
            projekt={projekt}
            version={version}
            onFertig={(id) => {
              setNeu(false);
              meldeAenderung();
              void lade();
              if (id) setOffenesTicket(id);
            }}
            onAbbrechen={() => setNeu(false)}
          />
        )}

        {offenesTicket && (
          <Ticketansicht
            id={offenesTicket}
            istVerwalter={istVerwalter}
            onZurueck={() => {
              setOffenesTicket(null);
              void lade();
            }}
          />
        )}

        {!neu && !offenesTicket && online && (
          <>
            <div className="reiter" style={{ marginTop: '1rem' }}>
              {(
                [
                  ['offen', 'Offen'],
                  ['erledigt', 'Erledigt'],
                  ['alle', 'Alle'],
                ] as const
              ).map(([wert, text]) => (
                <button key={wert} aria-pressed={ansicht === wert} onClick={() => setAnsicht(wert)}>
                  {text}
                  {wert === 'offen' && tickets
                    ? ` (${tickets.filter((t) => t.status !== 'erledigt').length})`
                    : ''}
                </button>
              ))}
            </div>

            {tickets === null ? (
              <p className="gedaempft">Einen Moment …</p>
            ) : sichtbar.length === 0 ? (
              <p className="gedaempft">
                {ansicht === 'offen' ? 'Nichts offen.' : 'Keine Rückmeldungen in dieser Ansicht.'}
              </p>
            ) : (
              <div className="ticketliste">
                {sichtbar.map((t) => (
                  <button key={t.id} className="ticketzeile" onClick={() => setOffenesTicket(t.id)}>
                    <span className={`ticketart ticketart--${t.art}`}>{ARTNAMEN[t.art]}</span>
                    <span className="ticketzeile__titel">
                      <strong>
                        #{t.nummer} {t.titel}
                      </strong>
                      <span className="klein gedaempft">
                        {t.erstellt_von_name ?? 'unbekannt'} · {datum(t.angelegt_am)}
                        {t.projekt_name ? ` · ${t.projekt_name}` : ''}
                        {t.anhaenge.length > 0 ? ` · ${t.anhaenge.length} Bild(er)` : ''}
                      </span>
                    </span>
                    <span className={`ticketstatus ticketstatus--${t.status}`}>
                      {STATUSNAMEN[t.status]}
                      {t.prioritaet === 'hoch' ? ' · hoch' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function NeuesTicket({
  projekt,
  version,
  onFertig,
  onAbbrechen,
}: {
  projekt: { id: string; name: string } | null;
  version: string;
  onFertig: (id: string | null) => void;
  onAbbrechen: () => void;
}): React.JSX.Element {
  const [art, setArt] = useState<Art>('fehler');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [prioritaet, setPrioritaet] = useState<Prioritaet>('normal');
  const [mitProjekt, setMitProjekt] = useState(projekt !== null);
  const [bilder, setBilder] = useState<File[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Ein Screenshot aus der Zwischenablage — am Rechner der schnellste Weg.
  useEffect(() => {
    const einfuegen = (ereignis: ClipboardEvent): void => {
      const dateien = [...(ereignis.clipboardData?.files ?? [])].filter((f) =>
        f.type.startsWith('image/'),
      );
      if (dateien.length > 0) setBilder((alt) => [...alt, ...dateien]);
    };
    window.addEventListener('paste', einfuegen);
    return () => window.removeEventListener('paste', einfuegen);
  }, []);

  async function absenden(): Promise<void> {
    if (!titel.trim()) {
      setFehler('Bitte in einer Zeile sagen, worum es geht.');
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await rufeServer('/api/tickets', {
        method: 'POST',
        body: JSON.stringify({
          titel,
          beschreibung,
          art,
          prioritaet,
          projektId: mitProjekt ? projekt?.id : null,
          kontext: {
            version,
            seite: window.location.pathname,
            geraet: navigator.userAgent,
            fenster: `${window.innerWidth} × ${window.innerHeight}`,
            beruehrung: window.matchMedia?.('(pointer: coarse)').matches ?? false,
            ...(mitProjekt && projekt ? { projekt: projekt.name } : {}),
          },
        }),
      });
      if (!antwort.ok) throw new Error(await fehlerAus(antwort));
      const ticket = (await antwort.json()) as Ticket;
      for (const bild of bilder) {
        const hoch = await rufeServer(
          `/api/tickets/${ticket.id}/anhang?name=${encodeURIComponent(bild.name || 'Bild')}`,
          { method: 'POST', body: bild, headers: { 'Content-Type': bild.type } },
        );
        if (!hoch.ok) throw new Error(`Ticket #${ticket.nummer} ist angelegt, aber ein Bild nicht: ${await fehlerAus(hoch)}`);
      }
      onFertig(ticket.id);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setLaeuft(false);
    }
  }

  return (
    <div className="kachel" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>Neue Rückmeldung</h3>
      <div className="knopfreihe">
        {(Object.keys(ARTNAMEN) as Art[]).map((wert) => (
          <button key={wert} aria-pressed={art === wert} onClick={() => setArt(wert)}>
            {ARTNAMEN[wert]}
          </button>
        ))}
      </div>
      <div className="feld" style={{ marginTop: '0.75rem' }}>
        <label htmlFor="tickettitel">Worum geht es — in einer Zeile</label>
        <input
          id="tickettitel"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="z. B. Traufabstand ändert nichts"
        />
      </div>
      <div className="feld">
        <label htmlFor="ticketbeschreibung">
          {art === 'fehler' ? 'Was hast du gemacht, was ist passiert, was hättest du erwartet?' : 'Beschreibung'}
        </label>
        <textarea
          id="ticketbeschreibung"
          rows={5}
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
        />
      </div>
      <div className="feld">
        <label>Dringlichkeit</label>
        <div className="knopfreihe">
          {(Object.keys(PRIORITAETSNAMEN) as Prioritaet[]).map((wert) => (
            <button key={wert} aria-pressed={prioritaet === wert} onClick={() => setPrioritaet(wert)}>
              {PRIORITAETSNAMEN[wert]}
            </button>
          ))}
        </div>
      </div>
      <div className="feld">
        <label htmlFor="ticketbild">Bildschirmfoto</label>
        <input
          id="ticketbild"
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setBilder((alt) => [...alt, ...(e.target.files ?? [])])}
        />
        <p className="klein gedaempft" style={{ margin: '0.3rem 0 0' }}>
          Am Rechner geht auch Einfügen aus der Zwischenablage (⌘V / Strg+V).
          {bilder.length > 0 ? ` ${bilder.length} Bild(er) ausgewählt.` : ''}
        </p>
      </div>
      {projekt && (
        <label className="klein" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={mitProjekt}
            onChange={(e) => setMitProjekt(e.target.checked)}
            style={{ width: 'auto', minHeight: 0 }}
          />
          Gehört zum Projekt „{projekt.name}“
        </label>
      )}
      {fehler && <div className="hinweis hinweis--fehler">{fehler}</div>}
      <div className="knopfreihe" style={{ marginTop: '0.75rem' }}>
        <button className="wichtig" disabled={laeuft} onClick={() => void absenden()}>
          {laeuft ? 'Wird gesendet …' : 'Absenden'}
        </button>
        <button onClick={onAbbrechen} disabled={laeuft}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function Ticketansicht({
  id,
  istVerwalter,
  onZurueck,
}: {
  id: string;
  istVerwalter: boolean;
  onZurueck: () => void;
}): React.JSX.Element {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [verlauf, setVerlauf] = useState<Verlaufseintrag[]>([]);
  const [bilder, setBilder] = useState<Record<string, string>>({});
  const [kommentar, setKommentar] = useState('');
  const [loesung, setLoesung] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);

  const lade = useCallback(async () => {
    const antwort = await rufeServer(`/api/tickets/${id}`);
    if (!antwort.ok) {
      setFehler(await fehlerAus(antwort));
      return;
    }
    const daten = (await antwort.json()) as { ticket: Ticket; verlauf: Verlaufseintrag[] };
    setTicket(daten.ticket);
    setVerlauf(daten.verlauf);
    setLoesung((alt) => alt || daten.ticket.loesung || '');
  }, [id]);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Bilder brauchen die Anmeldung — ein <img src> schickt sie nicht mit.
  useEffect(() => {
    if (!ticket) return;
    const urls: string[] = [];
    let aktiv = true;
    void (async () => {
      const neu: Record<string, string> = {};
      for (const a of ticket.anhaenge) {
        const antwort = await rufeServer(`/api/tickets/${id}/anhang/${a.id}`);
        if (!antwort.ok) continue;
        const url = URL.createObjectURL(await antwort.blob());
        urls.push(url);
        neu[a.id] = url;
      }
      if (aktiv) setBilder(neu);
    })();
    return () => {
      aktiv = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [ticket, id]);

  async function aendere(felder: Partial<Pick<Ticket, 'status' | 'prioritaet' | 'loesung'>>): Promise<void> {
    setFehler(null);
    const antwort = await rufeServer(`/api/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(felder),
    });
    if (!antwort.ok) {
      setFehler(await fehlerAus(antwort));
      return;
    }
    meldeAenderung();
    await lade();
  }

  async function kommentiere(): Promise<void> {
    if (!kommentar.trim()) return;
    const antwort = await rufeServer(`/api/tickets/${id}/kommentar`, {
      method: 'POST',
      body: JSON.stringify({ text: kommentar }),
    });
    if (!antwort.ok) {
      setFehler(await fehlerAus(antwort));
      return;
    }
    setKommentar('');
    await lade();
  }

  async function entferne(): Promise<void> {
    if (!ticket || !confirm(`Ticket #${ticket.nummer} endgültig entfernen?`)) return;
    const antwort = await rufeServer(`/api/tickets/${id}`, { method: 'DELETE' });
    if (!antwort.ok) {
      setFehler(await fehlerAus(antwort));
      return;
    }
    meldeAenderung();
    onZurueck();
  }

  if (!ticket) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <button onClick={onZurueck}>← Alle Rückmeldungen</button>
        {fehler ? <div className="hinweis hinweis--fehler">{fehler}</div> : <p>Einen Moment …</p>}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <button onClick={onZurueck}>← Alle Rückmeldungen</button>
      <h2 style={{ marginBottom: '0.2rem' }}>
        #{ticket.nummer} {ticket.titel}
      </h2>
      <p className="klein gedaempft" style={{ marginTop: 0 }}>
        {ARTNAMEN[ticket.art]} · von {ticket.erstellt_von_name ?? 'unbekannt'} am{' '}
        {datum(ticket.angelegt_am)}
        {ticket.projekt_name ? ` · Projekt ${ticket.projekt_name}` : ''}
        {ticket.kontext?.['version'] ? ` · Version ${String(ticket.kontext['version'])}` : ''}
      </p>

      {ticket.beschreibung && <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.beschreibung}</p>}

      {ticket.anhaenge.length > 0 && (
        <div className="ticketbilder">
          {ticket.anhaenge.map((a) =>
            bilder[a.id] ? (
              <a key={a.id} href={bilder[a.id]} target="_blank" rel="noreferrer">
                <img src={bilder[a.id]} alt={a.dateiname ?? 'Bildschirmfoto'} />
              </a>
            ) : (
              <span key={a.id} className="klein gedaempft">
                Bild lädt …
              </span>
            ),
          )}
        </div>
      )}

      <div className="feld" style={{ marginTop: '1rem' }}>
        <label>Status</label>
        <div className="knopfreihe">
          {(['offen', 'in_arbeit'] as const).map((wert) => (
            <button
              key={wert}
              aria-pressed={ticket.status === wert}
              onClick={() => void aendere({ status: wert })}
            >
              {STATUSNAMEN[wert]}
            </button>
          ))}
        </div>
      </div>

      <div className="feld">
        <label>Dringlichkeit</label>
        <div className="knopfreihe">
          {(Object.keys(PRIORITAETSNAMEN) as Prioritaet[]).map((wert) => (
            <button
              key={wert}
              aria-pressed={ticket.prioritaet === wert}
              onClick={() => void aendere({ prioritaet: wert })}
            >
              {PRIORITAETSNAMEN[wert]}
            </button>
          ))}
        </div>
      </div>

      <div className="feld">
        <label htmlFor="loesung">
          {ticket.status === 'erledigt' ? 'Erledigt — was getan wurde' : 'Erledigen: was wurde getan?'}
        </label>
        <textarea
          id="loesung"
          rows={3}
          value={loesung}
          onChange={(e) => setLoesung(e.target.value)}
          placeholder="Ohne diesen Satz lässt sich ein Ticket nicht erledigen."
        />
        <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
          <button
            className="wichtig"
            disabled={!loesung.trim()}
            onClick={() => void aendere({ status: 'erledigt', loesung })}
          >
            {ticket.status === 'erledigt' ? 'Lösung speichern' : 'Erledigt'}
          </button>
        </div>
      </div>

      {fehler && <div className="hinweis hinweis--fehler">{fehler}</div>}

      <h3>Verlauf</h3>
      <ul className="ticketverlauf">
        {verlauf.map((v, i) => (
          <li key={i}>
            <span className="klein gedaempft">
              {datum(v.zeit)} · {v.benutzer_name ?? 'unbekannt'}
            </span>
            <span>
              {v.aktion === 'angelegt'
                ? 'hat die Rückmeldung angelegt'
                : v.aktion === 'kommentar'
                  ? v.details
                  : v.aktion === 'erledigt'
                    ? `hat erledigt: ${v.details ?? ''}`
                    : v.aktion === 'status'
                      ? `Status: ${v.details ?? ''}`
                      : v.aktion === 'anhang'
                        ? `Bild angehängt: ${v.details ?? ''}`
                        : `geändert: ${v.details ?? ''}`}
            </span>
          </li>
        ))}
      </ul>
      <div className="feld">
        <textarea
          rows={2}
          value={kommentar}
          onChange={(e) => setKommentar(e.target.value)}
          placeholder="Kommentar oder Rückfrage"
        />
        <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
          <button disabled={!kommentar.trim()} onClick={() => void kommentiere()}>
            Kommentieren
          </button>
          {istVerwalter && (
            <button className="gefaehrlich" onClick={() => void entferne()}>
              Ticket entfernen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
